import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { NO_REFERENCE_SENTINEL, findCoveringSubscription } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"
import isModKeyPressed from "@utils/isModKeyPressed"
import replaceSlugs from "@utils/replaceSlugs"
import sanitizeHtml from "@utils/sanitizeHtml"

import { AccessCheck, Skeleton } from "@particles/index"

import { Badge, LinkButton } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import EmptyState from "@molecules/EmptyState/EmptyState"
import ValueDisplay from "@molecules/ValueDisplay/ValueDisplay"

import { NavTopBar } from "@organisms/index"

import { AppFrame } from "@templates/index"

import { useCatalogSubscriptionsQuery } from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"

import { CATALOG_ROUTES } from "@routes/catalogs"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./SettingsCatalogsItemDetails.module.scss"
import { useCatalogItemQuery } from "./SettingsCatalogsItemDetails.store"
import { formatCategoryPath, formatDimensions, formatMeasurement } from "./SettingsCatalogsItemDetails.utils"
import ItemGallery from "./components/ItemGallery/ItemGallery"
import ItemOptions from "./components/ItemOptions/ItemOptions"
import ItemPrices from "./components/ItemPrices/ItemPrices"
import ItemSubscriptionCTA from "./components/ItemSubscriptionCTA/ItemSubscriptionCTA"

function SettingsCatalogsItemDetailsContent() {
    const windowData = window as unknown as Record<string, string | undefined>
    const itemId = windowData.CATALOG_ITEM_ID ?? ""
    const catalogId = Number(windowData.CATALOG_ID)

    const { hasPermission } = useHasAccess()

    const [pricebookItemUpdateTemplate] = useSlugExtractor([PRICEBOOK_ITEMS_ROUTES.UPDATE])

    const itemQuery = useCatalogItemQuery(itemId)
    const subscriptionsQuery = useCatalogSubscriptionsQuery(catalogId)

    const item = itemQuery.data

    const subscriptions = subscriptionsQuery.data ?? []
    const isSubscribed =
        item !== undefined &&
        findCoveringSubscription(subscriptions, {
            id: item.id,
            brand: item.brand?.id ?? null,
            categories: item.categories,
        }) !== null

    // The page is reached through its brand listing, so that listing is where back leads.
    const brandItemsRoute = item
        ? replaceSlugs(CATALOG_ROUTES.BRAND_ITEMS, {
              catalog_id: String(catalogId),
              brand_id: String(item.brand?.id ?? NO_REFERENCE_SENTINEL),
          })
        : CATALOG_ROUTES.LIST
    const backDestination = hasPermission("pricebook_list_permission", PERMISSION_LEVEL.FULL)
        ? brandItemsRoute
        : undefined

    const renderPricebookItemValue = () => {
        if (!item?.link.pricebook_item_id) {
            return "Not imported"
        }

        const pricebookItemLabel = item.link.pricebook_item_description ?? "View PriceBook item"
        const pricebookItemRoute = replaceSlugs(pricebookItemUpdateTemplate, { id: item.link.pricebook_item_id })
        const handlePricebookItemClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            if (isModKeyPressed(event)) {
                window.open(pricebookItemRoute, "_blank", "noopener,noreferrer")
            } else {
                window.location.assign(pricebookItemRoute)
            }
        }

        return (
            <AccessCheck
                permissions={{ pricebook_edit_permission: PERMISSION_LEVEL.FULL }}
                placeholder={pricebookItemLabel}
            >
                <LinkButton colorScheme="blue" onClick={handlePricebookItemClick}>
                    {pricebookItemLabel}
                </LinkButton>
            </AccessCheck>
        )
    }

    const renderBody = () => {
        if (itemQuery.isLoading) {
            return (
                <div className={styles.skeleton}>
                    <Skeleton height={320} />
                </div>
            )
        }

        if (itemQuery.isError || !item) {
            return (
                <div className={styles.emptyStateContainer}>
                    <EmptyState
                        variant="error"
                        icon={faIcon({ name: "circle-xmark", style: "solid" })}
                        title="Hmm, something went wrong..."
                        supportText="An error occurred while fetching this catalog item."
                        primaryCTA={{ children: "Try again", onClick: () => void itemQuery.refetch() }}
                    />
                </div>
            )
        }

        const formattedWeight = formatMeasurement(item.weight)

        return (
            <div className={styles.layout}>
                <ItemGallery item={item} />
                <div className={styles.details}>
                    <div className={styles.header}>
                        <h1 className={styles.itemName}>{item.name}</h1>
                        {!subscriptionsQuery.isLoading && isSubscribed && (
                            <AlertBadge status="success" size="sm" showIcon={true} isLoading={false}>
                                Subscribed
                            </AlertBadge>
                        )}
                        {!item.is_active && (
                            <AlertBadge status="neutral" size="sm" showIcon={true} isLoading={false}>
                                No longer in catalog
                            </AlertBadge>
                        )}
                        <ItemSubscriptionCTA catalogId={catalogId} item={item} />
                    </div>
                    <ItemPrices item={item} />

                    <div className={styles.attributes}>
                        <ValueDisplay variant="vertical" label="SKU" showLabel={true} value={item.sku || "—"} />
                        <ValueDisplay
                            variant="vertical"
                            label="Brand"
                            showLabel={true}
                            value={item.brand?.name ?? "—"}
                        />
                        <ValueDisplay
                            variant="vertical"
                            label="Weight"
                            showLabel={true}
                            value={formattedWeight ? `${formattedWeight} ${item.weight_unit_display}` : "—"}
                        />
                        <ValueDisplay
                            variant="vertical"
                            label="Dimensions"
                            showLabel={true}
                            value={formatDimensions(item) || "—"}
                        />
                        <ValueDisplay
                            variant="vertical"
                            label="Linked PriceBook Item"
                            showLabel={true}
                            value={renderPricebookItemValue()}
                        />
                    </div>

                    {item.categories.length > 0 && (
                        <ValueDisplay
                            variant="vertical"
                            label="Categories"
                            showLabel={true}
                            value={
                                <ul className={styles.categoryList}>
                                    {item.categories.map((category) => (
                                        <li key={category.id}>
                                            <Badge size="sm">{formatCategoryPath(category)}</Badge>
                                        </li>
                                    ))}
                                </ul>
                            }
                        />
                    )}

                    {item.description && (
                        <div className={styles.description}>
                            <h2 className={styles.sectionTitle}>Description</h2>
                            <div
                                className={styles.descriptionBody}
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.description) }}
                            />
                        </div>
                    )}

                    <ItemOptions itemId={itemId} />
                </div>
            </div>
        )
    }

    return (
        <>
            <NavTopBar.Root>
                <NavTopBar.Header>
                    <NavTopBar.NavTopBarHeaderStart>
                        <NavTopBar.PageInnerTitle
                            label={item?.name ?? "Catalog Item"}
                            backDestination={backDestination}
                        />
                    </NavTopBar.NavTopBarHeaderStart>
                </NavTopBar.Header>
            </NavTopBar.Root>
            <AppFrame.PageContent>{renderBody()}</AppFrame.PageContent>
        </>
    )
}

export default function SettingsCatalogsItemDetails() {
    return (
        <AppFrame.Root navigationType="settings">
            <SettingsCatalogsItemDetailsContent />
        </AppFrame.Root>
    )
}
